import { api } from './api';
import type { DevicePlatform, PrintActionResponse, PrintJobStatus } from '../types';

export const detectDevicePlatform = (): DevicePlatform => {
    const userAgent = navigator.userAgent || '';

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'IOS';
    }

    if (/Android/i.test(userAgent)) {
        return 'ANDROID';
    }

    if (/Macintosh|Windows|Linux/i.test(userAgent)) {
        return 'DESKTOP';
    }

    return 'UNKNOWN';
};

const buildPrintableDocument = (html?: string, text?: string) => {
    const fallbackText = (text || '').replace(/[&<>"]/g, (value) => {
        switch (value) {
            case '&':
                return '&amp;';
            case '<':
                return '&lt;';
            case '>':
                return '&gt;';
            case '"':
                return '&quot;';
            default:
                return value;
        }
    });

    if (html && /<html[\s>]/i.test(html)) {
        return html;
    }

    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Impressao do pedido</title>
  <style>
    body { margin: 0; padding: 16px; font-family: Arial, sans-serif; color: #111; background: #fff; }
    pre { white-space: pre-wrap; word-break: break-word; margin: 0; font-family: "Courier New", monospace; }
  </style>
</head>
<body>
  ${html || `<pre>${fallbackText}</pre>`}
</body>
</html>`;
};

const printInHiddenFrame = (html?: string, text?: string) => {
    const printableDocument = buildPrintableDocument(html, text);
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.setAttribute('aria-hidden', 'true');

    const cleanup = () => {
        window.setTimeout(() => {
            frame.remove();
        }, 1000);
    };

    frame.onload = () => {
        const targetWindow = frame.contentWindow;
        if (!targetWindow) {
            cleanup();
            throw new Error('Nao foi possivel abrir o frame de impressao.');
        }

        targetWindow.focus();
        window.setTimeout(() => {
            targetWindow.print();
            cleanup();
        }, 150);
    };

    document.body.appendChild(frame);
    frame.srcdoc = printableDocument;
};

const openPrintableWindow = (html?: string, text?: string) => {
    const popup = window.open('', '_blank', 'noopener,noreferrer,width=480,height=720');
    if (!popup) {
        throw new Error('O navegador bloqueou a abertura da janela de impressao.');
    }

    const printableDocument = buildPrintableDocument(html, text);
    let hasPrinted = false;
    const triggerPrint = () => {
        if (hasPrinted || popup.closed) {
            return;
        }

        hasPrinted = true;
        popup.focus();
        window.setTimeout(() => {
            popup.print();
        }, 150);
    };

    popup.addEventListener('load', triggerPrint, { once: true });
    popup.document.open();
    popup.document.write(printableDocument);
    popup.document.close();
    window.setTimeout(triggerPrint, 400);
};

export const executePrintLaunchAction = async (action: PrintActionResponse) => {
    if (action.launchActionType === 'none') {
        return;
    }

    if (action.launchActionType === 'deep_link') {
        if (!action.launchUrl) {
            throw new Error('Deep link indisponivel para este provider.');
        }

        window.location.href = action.launchUrl;
        await new Promise((resolve) => window.setTimeout(resolve, 600));
        return;
    }

    if (action.launchActionType === 'share') {
        const payload = action.launchPayload || {};
        if (navigator.share) {
          await navigator.share({
            title: String(payload.title || 'Cupom de pedido'),
            text: String(payload.text || action.printableText || ''),
            url: typeof payload.url === 'string' ? payload.url : undefined,
          });
          return;
        }

        openPrintableWindow(action.printableHtml, action.printableText);
        return;
    }

    if (action.launchActionType === 'browser_print') {
        printInHiddenFrame(action.printableHtml, action.printableText);
        return;
    }

    throw new Error('Nenhuma acao de lancamento disponivel para este provider.');
};

export const updatePrintJobStatus = async (
    jobId: string,
    status: PrintJobStatus,
    devicePlatform: DevicePlatform,
    errorMessage?: string,
) => {
    await api.patch(`/print-jobs/${jobId}/status`, {
        status,
        errorMessage,
        devicePlatform,
    });
};
